import { useEffect, useState, Fragment } from "react";
import { ShieldCheck, SignOut, Buildings, Coins, FileText, PaperPlaneTilt, Bell } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money, shortDate, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import ProgramPlan from "@/components/ProgramPlan";

const STATUS_CLS = {
  submitted: "bg-sky-50 text-sky-700 border-sky-200",
  under_review: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-alert/10 text-alert border-alert/20",
};

const DOC_PILL = {
  pending: "bg-amber-50 text-amber-700 border-amber-300",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-300",
  rejected: "bg-alert/10 text-alert border-alert/40",
};

export default function GranteePortal() {
  const { user, logout } = useAuth();
  const [summary, setSummary] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [reimb, setReimb] = useState([]);
  const [form, setForm] = useState({ period_label: "", amount_requested: "", notes: "", adjustment: "" });
  const [busy, setBusy] = useState(false);
  const [replies, setReplies] = useState({});
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });

  const sendReply = async (rid) => {
    const msg = (replies[rid] || "").trim();
    if (!msg) return;
    try {
      await api.post(`/portal/reimbursements/${rid}/reply`, { message: msg });
      setReplies({ ...replies, [rid]: "" });
      toast.success("Reply sent");
      loadReimb(activeId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const markRead = async (r) => {
    if (!r.unread_count) return;
    try { await api.post(`/portal/reimbursements/${r.id}/mark-read`); loadReimb(activeId); } catch {}
  };

  const loadNotifs = async () => {
    try { setNotifs((await api.get("/notifications")).data); } catch {}
  };
  const markAll = async () => { await api.post("/notifications/read-all"); loadNotifs(); };
  const openNotif = async (n) => {
    if (!n.read) {
      setNotifs((p) => ({ ...p, unread: Math.max(0, p.unread - 1), items: p.items.map((x) => x.id === n.id ? { ...x, read: true } : x) }));
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
    }
    if (n.related_id && summary?.contracts?.some((c) => c.id === n.related_id)) setActiveId(n.related_id);
  };

  const loadSummary = async () => {
    try {
      const { data } = await api.get("/portal/summary");
      setSummary(data);
      if (!activeId && data.contracts.length) setActiveId(data.contracts[0].id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const loadReimb = async (id) => {
    if (!id) return;
    setReimb((await api.get(`/portal/contracts/${id}/reimbursements`)).data);
  };
  useEffect(() => { loadSummary(); loadNotifs(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadReimb(activeId); /* eslint-disable-next-line */ }, [activeId]);

  const submit = async () => {
    if (!form.period_label) return toast.error("Enter a period label (e.g. Q1 2026)");
    setBusy(true);
    try {
      await api.post(`/portal/contracts/${activeId}/reimbursements`, {
        period_label: form.period_label,
        amount_requested: parseFloat(form.amount_requested) || 0,
        notes: form.notes,
        adjustment: form.adjustment,
      });
      toast.success("Reimbursement submitted for review");
      setForm({ period_label: "", amount_requested: "", notes: "", adjustment: "" });
      loadReimb(activeId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const active = summary?.contracts?.find((c) => c.id === activeId);

  const uploadDoc = async (rid, fileList) => {
    if (!fileList || !fileList[0]) return;
    const fd = new FormData();
    fd.append("file", fileList[0]);
    try {
      await api.post(`/portal/reimbursements/${rid}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document attached");
      loadReimb(activeId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="h-16 bg-primary text-white flex items-center justify-between px-4 sm:px-8 sticky top-0 z-20">
        <div className="flex items-center gap-2">
          <ShieldCheck size={26} weight="fill" className="text-gold" />
          <div>
            <div className="font-head font-bold text-sm leading-none">grantSME · Grantee Portal</div>
            <div className="text-[10px] text-white/60 mt-1">{summary?.org_name}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="portal-notifications-bell" className="relative p-2 rounded-md hover:bg-white/10 transition-colors">
                <Bell size={20} className="text-white" />
                {notifs.unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-gold text-primary text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">{notifs.unread}</span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[90vw] max-w-96 p-0">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <span className="font-head font-semibold text-sm">Notifications</span>
                <button data-testid="portal-mark-all-read" onClick={markAll} className="text-xs text-primary hover:underline">Mark all read</button>
              </div>
              <ScrollArea className="max-h-80">
                {notifs.items.length === 0 && <div className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications yet</div>}
                {notifs.items.map((n) => (
                  <button key={n.id} data-testid={`portal-notif-${n.id}`} onClick={() => openNotif(n)} className={`w-full text-left px-4 py-3 border-b border-border/60 hover:bg-secondary transition-colors cursor-pointer ${!n.read ? "bg-secondary/60" : ""}`}>
                    <div className="text-sm font-medium text-foreground">{n.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{n.message}</div>
                    <div className="text-[10px] text-muted-foreground/70 mt-1">{n.created_at?.slice(0, 16).replace("T", " ")}</div>
                  </button>
                ))}
              </ScrollArea>
            </PopoverContent>
          </Popover>
          <div className="text-right hidden sm:block">
            <div className="text-sm font-medium leading-none">{user?.name}</div>
            <div className="text-[11px] text-white/60 mt-0.5">{user?.email}</div>
          </div>
          <Button data-testid="portal-logout" variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={logout}><SignOut size={18} /></Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 sm:p-8">
        <div className="overline text-muted-foreground">Welcome back</div>
        <h1 className="font-head font-bold text-2xl tracking-tight mb-6">{user?.name}</h1>

        {summary?.grants?.length > 0 && (
          <div className="mb-8">
            <div className="overline text-muted-foreground mb-2 flex items-center gap-1.5"><Coins size={14} /> Your Grants</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {summary.grants.map((g) => (
                <div key={g.id} data-testid={`portal-grant-${g.id}`} className="bg-card border border-border rounded-md p-4">
                  <div className="font-medium text-foreground">{g.title}</div>
                  <div className="text-xs text-muted-foreground mt-1">{g.funding_source}</div>
                  <div className="text-sm font-semibold mt-2">{money(g.grant_amount)}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">{shortDate(g.grant_period_start)} → {shortDate(g.grant_period_end)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="overline text-muted-foreground mb-2 flex items-center gap-1.5"><FileText size={14} /> Your Contracts</div>
        {(!summary || summary.contracts.length === 0) ? (
          <div className="bg-card border border-border rounded-md p-8 text-center text-sm text-muted-foreground">
            <Buildings size={28} className="mx-auto mb-2 text-muted-foreground/50" />
            No contracts have been shared with you yet. Your program officer will link records to your account.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {summary.contracts.map((c) => (
                <button key={c.id} data-testid={`portal-contract-tab-${c.id}`} onClick={() => setActiveId(c.id)}
                  className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${activeId === c.id ? "bg-primary text-white border-primary" : "bg-card border-border hover:border-primary"}`}>
                  {c.title}
                </button>
              ))}
            </div>

            {active && (
              <div className="grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-1 bg-card border border-border rounded-md p-5 h-fit">
                  <div className="font-head font-semibold">{active.title}</div>
                  <div className="text-xs text-muted-foreground">{active.contract_number}</div>
                  <dl className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between"><dt className="text-muted-foreground">Amount</dt><dd className="font-medium">{money(active.amount)}</dd></div>
                    <div className="flex justify-between"><dt className="text-muted-foreground">Program</dt><dd>{active.program || "—"}</dd></div>
                    <div className="flex justify-between"><dt className="text-muted-foreground">Period</dt><dd>{shortDate(active.start_date)} → {shortDate(active.end_date)}</dd></div>
                    <div className="flex justify-between"><dt className="text-muted-foreground">Status</dt><dd className="capitalize">{active.status}</dd></div>
                  </dl>
                </div>

                <div className="lg:col-span-2 space-y-4">
                  <div className="bg-card border border-border rounded-md p-5" data-testid="portal-program-plan">
                    <div className="font-head font-semibold text-sm mb-3">Program Plan &amp; Logic Model</div>
                    <ProgramPlan contractId={active.id} isStaff={false} />
                  </div>

                  <div className="bg-card border border-border rounded-md p-5">
                    <div className="font-head font-semibold text-sm mb-3">Submit a reimbursement request</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label>Period</Label><Input data-testid="portal-reimb-period" value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} placeholder="e.g. Q1 2026" /></div>
                      <div className="space-y-1.5"><Label>Amount requested ($)</Label><Input data-testid="portal-reimb-amount" type="number" value={form.amount_requested} onChange={(e) => setForm({ ...form, amount_requested: e.target.value })} /></div>
                      <div className="sm:col-span-2 space-y-1.5"><Label>Notes</Label><Textarea data-testid="portal-reimb-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything the reviewer should know…" /></div>
                      <div className="sm:col-span-2 space-y-1.5">
                        <Label>What are you adjusting this period, and why?</Label>
                        <Textarea data-testid="portal-reimb-adjustment" value={form.adjustment} onChange={(e) => setForm({ ...form, adjustment: e.target.value })} placeholder="Compare progress to your program plan's Part 4 targets. What's on track, what's behind, and what will you change next quarter?" />
                        <p className="text-[11px] text-muted-foreground">Tracked against your program plan — open the Program Plan above to review this year's targets.</p>
                      </div>
                    </div>
                    <Button className="mt-3" data-testid="portal-reimb-submit" onClick={submit} disabled={busy}>
                      <PaperPlaneTilt size={15} className="mr-1.5" /> {busy ? "Submitting…" : "Submit for review"}
                    </Button>
                  </div>

                  <div className="bg-card border border-border rounded-md overflow-x-auto">
                    <div className="px-5 pt-4 font-head font-semibold text-sm">Submission history</div>
                    {reimb.length === 0 ? (
                      <div className="px-5 py-6 text-sm text-muted-foreground">No submissions yet.</div>
                    ) : (
                      <Table>
                        <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Amount</TableHead><TableHead>Submitted</TableHead><TableHead>Status</TableHead><TableHead>Documents</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {reimb.map((r) => (
                            <Fragment key={r.id}>
                            <TableRow data-testid={`portal-reimb-row-${r.id}`}>
                              <TableCell className="text-sm">
                                <div className="flex items-center gap-2">
                                  {r.period_label}
                                  {r.unread_count > 0 && (
                                    <span data-testid={`portal-unread-${r.id}`} className="bg-alert text-white rounded-full h-5 px-1.5 text-[10px] font-semibold inline-flex items-center">{r.unread_count} new</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm font-medium">{money(r.amount_requested)}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{shortDate(r.date_submitted)}</TableCell>
                              <TableCell><span className={`text-[11px] px-2 py-0.5 rounded-sm border capitalize ${STATUS_CLS[r.status] || "bg-secondary text-foreground border-border"}`}>{(r.status || "").replace("_", " ")}</span></TableCell>
                              <TableCell className="text-xs">
                                <div className="flex flex-col gap-1">
                                  {(r.documents || []).map((d) => (
                                    <span key={d.id} className="flex items-center gap-1.5" data-testid={`portal-doc-${d.id}`}>
                                      <span className="text-muted-foreground truncate max-w-[140px]">{d.file_name}</span>
                                      <span data-testid={`portal-doc-status-${d.id}`} className={`text-[10px] px-1.5 py-0.5 rounded-sm border capitalize ${DOC_PILL[d.review_status || "pending"]}`}>{d.review_status || "pending"}</span>
                                    </span>
                                  ))}
                                  <label className="text-primary cursor-pointer hover:underline" data-testid={`portal-attach-${r.id}`}>
                                    + Attach
                                    <input type="file" className="hidden" onChange={(e) => uploadDoc(r.id, e.target.files)} />
                                  </label>
                                </div>
                              </TableCell>
                            </TableRow>
                            {(r.adjustment || "").trim() && (
                              <TableRow className="hover:bg-transparent">
                                <TableCell colSpan={5} className="pt-0 pb-2">
                                  <div className="text-xs bg-gold/10 border-l-2 border-gold rounded-sm px-3 py-2" data-testid={`portal-reimb-adjustment-${r.id}`}>
                                    <span className="font-medium text-foreground">Adjustments this period: </span>
                                    <span className="text-muted-foreground">{r.adjustment}</span>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                            {((r.thread || []).length > 0 || (r.notes || "").trim()) && (
                              <TableRow data-testid={`portal-thread-${r.id}`} className="bg-secondary/40 hover:bg-secondary/40">
                                <TableCell colSpan={5} className="py-2">
                                  <div className="overline text-muted-foreground mb-1.5">Conversation with your reviewer</div>
                                  <div className="space-y-1.5 mb-2">
                                    {(r.thread || []).map((m, i) => (
                                      <div key={i} className={`text-sm flex gap-2 ${m.by_role === "grantee" ? "text-foreground" : "text-primary"}`}>
                                        <span className="font-medium whitespace-nowrap">{m.by_name} ({m.by_role === "grantee" ? "You" : "Reviewer"}):</span>
                                        <span>{m.message}</span>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="flex gap-2">
                                    <Input data-testid={`portal-reply-input-${r.id}`} value={replies[r.id] || ""} onFocus={() => markRead(r)} onChange={(e) => setReplies({ ...replies, [r.id]: e.target.value })} placeholder="Reply to your reviewer…" className="h-8" />
                                    <Button size="sm" className="h-8" data-testid={`portal-reply-send-${r.id}`} onClick={() => sendReply(r.id)}>Send</Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                            </Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
